import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Collapse,
  Icon,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
} from '@material-ui/core';
import { deleteAutoQuoteScheduleApi, editAutoQuoteScheduleApi } from 'api/automatedQuotes';
import { format } from 'date-fns';
import { convertUTCDateToLocalDate } from 'helpers/date';
import { green, red } from '@material-ui/core/colors';
import { CheckCircle, Cancel } from '@material-ui/icons';
import { TextFormattedNumber } from 'components';
import { useStyles } from '../styles';
import { cronToHumanReadable } from '../helpers';
import QuoteScheduleDetails from './QuoteScheduleDetails';


const QuoteScheduleTableRow = ({ quote, headTableCells }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [openRow, setOpenRow] = useState(false);

  const { autoQuotes } = useSelector((state: IReduxState) => state.automatedQuotes);

  const {
    _id,
    quoteDiscount: { price, price_type },
    startAfterDays,
    mustUsedBeforeDays,
    period,
    lastRun,
    isEnabled,
    runImmediately,
    akwaRuleId,
    quoteName,
    expireAfterDays,
    selectedProducts,
    patientNotice,
    createdBy,
  } = quote;

  const columns = headTableCells.reduce((acc, column) => {
    acc[column.label] = column;
    return acc;
  }, {});

  const selectedRule = autoQuotes?.find((list) => list.id == akwaRuleId);

  const onPauseSchedule = (schedule) => {
    const updatedSchedule = {...schedule, isEnabled: !schedule.isEnabled};
    editAutoQuoteScheduleApi({dispatch, schedule: updatedSchedule});
  };

  const onDeleteSchedule = () => {
    deleteAutoQuoteScheduleApi({dispatch, id: _id});
  };

  const calculateMessage = (days, label) => {
    if (!days || isNaN(parseInt(days, 10))) {
      return 'N/A';
    }

    return `It will ${label} after ${days} ${days == 1 ? 'day' : 'days'} from today.`;
  };

  return (
    <>
      <TableRow className={`${openRow ? classes.blueCell : ''} ${classes.noBorders}`}>
        {columns['Quote Name']?.display && <TableCell>{quoteName}</TableCell>}
        {columns['Start Date']?.display && (
          <TableCell>
            {startAfterDays ? calculateMessage(startAfterDays, 'start') : 'N/A'}
          </TableCell>
        )}
        {columns['Expiration Date']?.display && (
          <TableCell>
            {expireAfterDays ? calculateMessage(expireAfterDays, 'expire') : 'N/A'}
          </TableCell>
        )}
        {columns['Must Used Date']?.display && (
          <TableCell>
            {mustUsedBeforeDays ? calculateMessage(mustUsedBeforeDays, 'must be used') : 'N/A'}
          </TableCell>
        )}
        {columns['Quote Discount']?.display && (
          <TableCell align="center">
            {price === 0 && 'N/A'}
            {price !== 0 && price_type === 'fixed' && (
              <TextFormattedNumber value={price} prefix="$" />
            )}
            {price !== 0 && price_type !== 'fixed' && `${price} %`}
          </TableCell>
        )}
        {columns['Period']?.display && <TableCell>{cronToHumanReadable(period)}</TableCell>}
        {columns['Last Run']?.display && (
          <TableCell>
            {lastRun ? format(convertUTCDateToLocalDate(lastRun), 'MM/dd/yyyy') : 'N/A'}
          </TableCell>
        )}
        {columns['Selected Rule']?.display && (
          <TableCell>{selectedRule?.name}</TableCell>
        )}
        {columns['Run Immediately']?.display && (
          <TableCell align="center">
            {runImmediately ? (
              <CheckCircle style={{ fill: green[500] }} />
            ) : (
              <Cancel style={{ fill: red[500] }} />
            )}
          </TableCell>
        )}
        {columns['Is Active']?.display && (
          <TableCell align="center">
            {isEnabled ? (
              <CheckCircle style={{ fill: green[500] }} />
            ) : (
              <Cancel style={{ fill: red[500] }} />
            )}
          </TableCell>
        )}
        {columns['Created By']?.display && (
          <TableCell className="whitespace-no-wrap">
            {createdBy}
          </TableCell>
        )}
        <TableCell className="whitespace-no-wrap">
          <Tooltip title={`${isEnabled ? 'Pause' : 'Run'} Schedule`}>
            <IconButton
              size="small"
              onClick={() => onPauseSchedule(quote)}
            >
              <Icon>{isEnabled ? 'pause' : 'play_arrow'}</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Schedule">
            <IconButton
              size="small"
              onClick={() => onDeleteSchedule()}
            >
              <Icon>delete</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title={`${openRow ? 'View Less' : 'View More'}`}>
            <Button
              size="small"
              color="primary"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setOpenRow(!openRow);
              }}
              endIcon={
                <Icon fontSize="small">
                  {openRow ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}
                </Icon>
              }
            >
              {openRow ? 'View Less' : 'View More'}
            </Button>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={Object.keys(headTableCells).length + 1}
          padding="none"
          className={`${openRow ? classes.blueCell : ''}`}
        >
          <Collapse in={openRow} timeout="auto" unmountOnExit>
            <QuoteScheduleDetails notes={patientNotice} products={selectedProducts} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default QuoteScheduleTableRow;
